/**
 * HostFormDrawer — Story 2.2 (AC-2): Host-Detail als Drawer mit
 * Bearbeitungsformular (ModalSheet variant="drawer").
 *
 * Felder laut Contract (Story 2.1): Name, E-Mail, Telefon, Solana-Wallet,
 * Notizen, Währungspräferenz. Validierungs-Feedback aus 422-Responses wird
 * feldgenau angezeigt (parseHostValidationError); die Solana-Base58-Prüfung
 * macht bewusst NUR das Backend — das Frontend erfindet keine eigene Regel.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ModalSheet } from '../../components/ui/ModalSheet'
import { formatNumber } from '../../components/ui/format'
import type { Host, Settlement, HostSplitRule } from './useHosts'
import { useCreateHost, useUpdateHost, useDeleteHost } from './useHostMutations'
import type { HostFormData } from './useHostMutations'
import {
  parseHostValidationError,
  hostErrorMessage,
  type HostFormField,
} from './hostValidation'

const CURRENCIES = ['HNT', 'EUR', 'USD'] as const
type Currency = (typeof CURRENCIES)[number]

/**
 * Story frontend-host-details (#23): Anreicherung fuer die read-only
 * Detail-Zusammenfassung im Bearbeitungs-Drawer — Hotspot-Zuordnung und
 * volle Abrechnungshistorie des Hosts (nicht nur die juengste Abrechnung
 * wie in der Listenzeile).
 */
export type HostDetailData = {
  hotspotIds: number[]
  hotspotNames: Map<string, string>
  settlements: Settlement[]
  /** Issue web#23: geltende Provisions-Regeln dieses Hosts (Anteil %). */
  rules?: HostSplitRule[]
}

type Props = {
  open: boolean
  /** Vorhandener Host = Bearbeiten (+ Löschen); undefined = Anlegen. */
  host?: Host
  /** Nur im Edit-Modus: Hotspot-Zuordnung + Abrechnungen dieses Hosts. */
  detail?: HostDetailData
  onClose: () => void
}

const BALANCE_STATUSES: NonNullable<Settlement['status']>[] = ['draft', 'approved']
const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', HNT: 'HNT' }

function fmtSettlementAmount(raw: string | undefined, currency: string | undefined): string {
  const value = Number(raw ?? '')
  if (!Number.isFinite(value)) return '—'
  const symbol = currency ? (CURRENCY_SYMBOL[currency] ?? currency) : ''
  const num = formatNumber(value, 2)
  return symbol ? `${num} ${symbol}` : num
}

/** Offener Saldo (draft + approved) je Waehrung, ueber ALLE Abrechnungen des Hosts. */
function sumOpenBalance(settlements: Settlement[]): string[] {
  const byCurrency = new Map<string, number>()
  for (const s of settlements) {
    if (!s.status || !BALANCE_STATUSES.includes(s.status)) continue
    const value = Number(s.total_amount ?? '')
    if (!Number.isFinite(value)) continue
    const cur = s.currency ?? ''
    byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + value)
  }
  return [...byCurrency.entries()].map(
    ([cur, value]) => `${formatNumber(value, 2)} ${CURRENCY_SYMBOL[cur] ?? cur}`,
  )
}

/**
 * Read-only Zusammenfassung (Story #23): beantwortet "Welche Hotspots?" und
 * "Was schulde ich?", OHNE dass zwingend das Bearbeiten-Formular durchsucht
 * werden muss. Die Hotspot-Zuweisung selbst passiert bewusst NICHT hier
 * (Story #4) — der Link fuehrt zum bestehenden Provisions-Regel-Editor
 * (/splits), damit die Zuordnungslogik nicht dupliziert wird.
 */
/** Stammdaten-Zeile — read-only, Feld ohne Wert zeigt den globalen Platzhalter. */
function StammdatenRow({ label, value }: { label: string; value: string | null | undefined }) {
  const { t } = useTranslation('hosts')
  return (
    <div className="hosts-detail-field">
      <span className="hosts-detail-field-label">{label}</span>
      <span className="hosts-detail-field-value">{value || t('list.noValue')}</span>
    </div>
  )
}

function HostDetailSummary({ host, detail }: { host: Host; detail: HostDetailData }) {
  const { t } = useTranslation('hosts')
  const hostId = host.id as number
  const openBalance = sumOpenBalance(detail.settlements)
  const sortedSettlements = [...detail.settlements].sort((a, b) =>
    (b.period_month ?? '').localeCompare(a.period_month ?? ''),
  )
  const rules = detail.rules ?? []

  return (
    <div className="hosts-detail" data-testid="host-detail-summary">
      <div className="hosts-detail-block">
        <div className="hosts-form-label">{t('detail.masterDataTitle')}</div>
        <div className="hosts-detail-fields">
          <StammdatenRow label={t('form.email')} value={host.email} />
          <StammdatenRow label={t('form.phone')} value={host.phone} />
          <StammdatenRow label={t('form.wallet')} value={host.solana_wallet_address} />
          <StammdatenRow label={t('form.currency')} value={host.currency_preference} />
        </div>
        {host.notes && <p className="muted hosts-detail-notes">{host.notes}</p>}
      </div>

      <div className="hosts-detail-block">
        <div className="hosts-form-label">{t('detail.rulesTitle')}</div>
        {rules.length > 0 ? (
          <ul className="hosts-detail-settlements" data-testid="host-detail-rules">
            {rules.map((rule) => {
              const hotspotName =
                rule.hotspot_id !== undefined
                  ? (detail.hotspotNames.get(String(rule.hotspot_id)) ??
                    t('list.hotspotFallback', { id: rule.hotspot_id }))
                  : t('list.noValue')
              const share =
                rule.percentage !== undefined && rule.percentage !== null
                  ? t('detail.rulePercentage', { percent: formatNumber(rule.percentage, 1) })
                  : fmtSettlementAmount(
                      rule.fixed_amount != null ? String(rule.fixed_amount) : undefined,
                      rule.payout_currency,
                    )
              return (
                <li key={rule.id} data-testid={`host-detail-rule-${rule.id}`}>
                  <span>{hotspotName}</span>
                  <span>{share}</span>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="muted hosts-detail-empty">{t('detail.noRules')}</p>
        )}
      </div>

      <div className="hosts-detail-block">
        <div className="hosts-form-label">{t('detail.hotspotsTitle')}</div>
        {detail.hotspotIds.length > 0 ? (
          <div className="row hosts-chip-row">
            {detail.hotspotIds.map((id) => (
              <span className="hosts-hs-chip" key={id}>
                {detail.hotspotNames.get(String(id)) ?? t('list.hotspotFallback', { id })}
              </span>
            ))}
          </div>
        ) : (
          <p className="muted hosts-detail-empty">{t('detail.noHotspots')}</p>
        )}
        <Link
          to="/splits/create"
          search={{ host_id: hostId }}
          className="hosts-detail-link"
          data-testid="host-detail-assign-link"
        >
          {t('detail.assignHotspot')}
        </Link>
      </div>

      <div className="hosts-detail-block">
        <div className="hosts-form-label">{t('detail.balanceTitle')}</div>
        <div className="num-sm hosts-detail-balance" data-testid="host-detail-open-balance">
          {openBalance.length > 0 ? openBalance.join(' · ') : t('detail.noOpenBalance')}
        </div>

        {sortedSettlements.length > 0 ? (
          <ul className="hosts-detail-settlements">
            {sortedSettlements.map((s) => (
              <li key={s.id} data-testid={`host-detail-settlement-${s.id}`}>
                <span>{s.period_month ?? '—'}</span>
                <span>{t(`payout.${s.status ?? 'none'}.label`)}</span>
                <span>{fmtSettlementAmount(s.total_amount, s.currency)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted hosts-detail-empty">{t('detail.noSettlements')}</p>
        )}
      </div>
    </div>
  )
}

/**
 * Story #23: Aktionen der read-only Detailansicht — "Bearbeiten" und
 * "Löschen" sind EIGENE Buttons, kein impliziter Default mehr. Löschen ist
 * wie zuvor zweistufig (erst Bestaetigung, dann Mutation).
 */
function HostDetailActions({
  host,
  detail,
  onEdit,
  onDeleted,
}: {
  host: Host
  detail: HostDetailData
  onEdit: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation('hosts')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const deleteHost = useDeleteHost()
  const ruleCount = detail.rules?.length ?? 0
  const settlementCount = detail.settlements.length

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setError(null)
    try {
      await deleteHost.mutateAsync(host.id as number)
      onDeleted()
    } catch (deleteError) {
      setError(hostErrorMessage(deleteError) ?? t('form.deleteFailed'))
      setConfirmDelete(false)
    }
  }

  return (
    <div className="hosts-detail-actions">
      {error && (
        <div className="hint hosts-form-banner" role="alert" data-testid="host-detail-error">
          {error}
        </div>
      )}
      <div className="row hosts-form-actions">
        <button
          type="button"
          className="btn btn-sm hosts-form-delete"
          onClick={() => void handleDelete()}
          disabled={deleteHost.isPending}
          data-testid="host-detail-delete"
        >
          {confirmDelete
            ? t('form.deleteConfirmCounts', { ruleCount, settlementCount })
            : t('form.delete')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onEdit}
          data-testid="host-detail-edit"
        >
          {t('detail.edit')}
        </button>
      </div>
    </div>
  )
}

type FieldErrors = Partial<Record<HostFormField, string>>

function isCurrency(value: string): value is Currency {
  return (CURRENCIES as readonly string[]).includes(value)
}

function HostForm({ host, onClose }: { host?: Host; onClose: () => void }) {
  const { t } = useTranslation('hosts')
  const isEdit = host?.id !== undefined

  const [name, setName] = useState(host?.name ?? '')
  const [email, setEmail] = useState(host?.email ?? '')
  const [phone, setPhone] = useState(host?.phone ?? '')
  const [wallet, setWallet] = useState(host?.solana_wallet_address ?? '')
  const [notes, setNotes] = useState(host?.notes ?? '')
  const [currency, setCurrency] = useState<string>(host?.currency_preference ?? '')

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Story #4: nach dem Anlegen (nicht beim Bearbeiten) zeigt der Erfolgs-
  // Zustand die CTA zur Provisions-Regel statt den Drawer sofort zu schliessen.
  const [createdHost, setCreatedHost] = useState<Host | null>(null)

  const createHost = useCreateHost()
  const updateHost = useUpdateHost()
  const deleteHost = useDeleteHost()
  const busy = createHost.isPending || updateHost.isPending || deleteHost.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    if (!name.trim()) {
      setFieldErrors({ name: t('form.nameRequired') })
      return
    }
    setFieldErrors({})

    const data: HostFormData = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      solana_wallet_address: wallet.trim() || null,
      notes: notes.trim() || null,
      currency_preference: isCurrency(currency) ? currency : null,
    }

    try {
      if (isEdit) {
        await updateHost.mutateAsync({ id: host.id as number, data })
        // Drawer schliesst im Edit-Modus wie bisher direkt.
        onClose()
      } else {
        const created = await createHost.mutateAsync(data)
        // Story #4: Erfolgs-Zustand mit CTA zur Provisions-Regel statt
        // sofortigem Schliessen — der neue Host hat noch keine Zuordnung.
        // Ohne Host-Id aus der Antwort (Rand-Fall) bleibt es beim alten
        // Verhalten und der Drawer schliesst direkt.
        if (created?.id !== undefined) {
          setCreatedHost(created)
        } else {
          onClose()
        }
      }
    } catch (error) {
      const validation = parseHostValidationError(error)
      if (validation) {
        setFieldErrors(validation.fields)
        setFormError(validation.message ?? t('form.validationFailed'))
      } else {
        setFormError(hostErrorMessage(error) ?? t('form.saveFailed'))
      }
    }
  }

  async function handleDelete() {
    if (!isEdit) return
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setFormError(null)
    try {
      await deleteHost.mutateAsync(host.id as number)
      onClose()
    } catch (error) {
      setFormError(hostErrorMessage(error) ?? t('form.deleteFailed'))
      setConfirmDelete(false)
    }
  }

  function field(
    fieldName: HostFormField,
    label: string,
    input: React.ReactNode,
    hint?: string,
  ) {
    const error = fieldErrors[fieldName]
    return (
      <div className="hosts-form-field">
        <label className="hosts-form-label" htmlFor={`host-field-${fieldName}`}>
          {label}
        </label>
        {input}
        {error ? (
          <p
            className="hosts-form-error"
            role="alert"
            data-testid={`host-error-${fieldName}`}
          >
            {error}
          </p>
        ) : (
          hint && <p className="hosts-form-hint">{hint}</p>
        )}
      </div>
    )
  }

  if (createdHost) {
    return (
      <div className="hosts-detail" data-testid="host-create-success">
        <p className="hosts-form-hint">
          {t('form.createdHint', { name: createdHost.name ?? '' })}
        </p>
        <Link
          to="/splits/create"
          search={{ host_id: createdHost.id as number }}
          className="hosts-detail-link"
          data-testid="host-create-assign-link"
        >
          {t('detail.assignHotspot')}
        </Link>
        <div className="hosts-form-actions">
          <div className="hosts-form-actions-main">
            <button type="button" className="btn btn-sm btn-primary" onClick={onClose}>
              {t('form.done')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} data-testid="host-form">
      {formError && (
        <div className="hint hosts-form-banner" role="alert" data-testid="host-form-error">
          {formError}
        </div>
      )}

      {field(
        'name',
        t('form.name'),
        <input
          id="host-field-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={fieldErrors.name ? true : undefined}
          data-testid="host-input-name"
        />,
      )}

      {field(
        'email',
        t('form.email'),
        <input
          id="host-field-email"
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={fieldErrors.email ? true : undefined}
          data-testid="host-input-email"
        />,
      )}

      {field(
        'phone',
        t('form.phone'),
        <input
          id="host-field-phone"
          className="input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-invalid={fieldErrors.phone ? true : undefined}
          data-testid="host-input-phone"
        />,
      )}

      {field(
        'solana_wallet_address',
        t('form.wallet'),
        <input
          id="host-field-solana_wallet_address"
          className="input mono"
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          aria-invalid={fieldErrors.solana_wallet_address ? true : undefined}
          spellCheck={false}
          data-testid="host-input-wallet"
        />,
        t('form.walletHint'),
      )}

      {field(
        'currency_preference',
        t('form.currency'),
        <select
          id="host-field-currency_preference"
          className="input"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          aria-invalid={fieldErrors.currency_preference ? true : undefined}
          data-testid="host-input-currency"
        >
          <option value="">{t('form.currencyNone')}</option>
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>,
      )}

      {field(
        'notes',
        t('form.notes'),
        <textarea
          id="host-field-notes"
          className="input hosts-form-notes"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          aria-invalid={fieldErrors.notes ? true : undefined}
          data-testid="host-input-notes"
        />,
      )}

      <div className="hosts-form-actions">
        {isEdit && (
          <button
            type="button"
            className="btn btn-sm hosts-form-delete"
            onClick={() => void handleDelete()}
            disabled={busy}
            data-testid="host-delete"
          >
            {confirmDelete ? t('form.deleteConfirm') : t('form.delete')}
          </button>
        )}
        <div className="hosts-form-actions-main">
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy}>
            {t('form.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={busy}
            data-testid="host-submit"
          >
            {busy
              ? t('form.saving')
              : isEdit
                ? t('form.save')
                : t('form.createSubmit')}
          </button>
        </div>
      </div>
    </form>
  )
}

/**
 * Story #23: Der erste Klick auf eine Host-Zeile zeigt IMMER zuerst die
 * read-only Detailansicht — "Bearbeiten" ist eine eigene Aktion, kein
 * Default mehr. Ohne `detail`-Daten (z. B. isolierte Formular-Tests) faellt
 * dies auf das reine Formular zurueck.
 */
function HostDrawerBody({
  host,
  detail,
  onClose,
}: {
  host?: Host
  detail?: HostDetailData
  onClose: () => void
}) {
  const isEdit = host?.id !== undefined
  const [mode, setMode] = useState<'detail' | 'edit'>('detail')

  if (isEdit && detail && mode === 'detail') {
    return (
      <>
        <HostDetailSummary host={host} detail={detail} />
        <HostDetailActions
          host={host}
          detail={detail}
          onEdit={() => setMode('edit')}
          onDeleted={onClose}
        />
      </>
    )
  }
  return <HostForm host={host} onClose={onClose} />
}

export function HostFormDrawer({ open, host, detail, onClose }: Props) {
  const { t } = useTranslation('hosts')
  const isEdit = host?.id !== undefined

  return (
    <ModalSheet
      open={open}
      onClose={onClose}
      variant="drawer"
      size="md"
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      description={isEdit ? t('form.editDescription') : t('form.createDescription')}
      data-testid="host-drawer"
    >
      {/* key erzwingt Reset (Detail- statt Edit-Modus) beim Wechsel zwischen
          Hosts / Neu-Anlage. */}
      <HostDrawerBody key={host?.id ?? 'new'} host={host} detail={detail} onClose={onClose} />
    </ModalSheet>
  )
}
